import { memo } from 'react';
import { twJoin } from 'tailwind-merge';
import moment from 'moment';

import { subheading2 } from '@/styles/styles';
import Lesson from '@/models/Lesson';
import { Nullable } from '@/models/Model';
import { ArrowTopRightOnSquareIcon, LoadingIcon } from '@/components/icons';
import Countdown from '@/components/ui/custom/Countdown';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/utils/routes';
import { useMediaQuery } from 'react-responsive';
import { Breakpoints } from '@/utils/Breakpoint';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { IconWithText } from '@/components/ui/custom/dataDisplay/IconWithText';
import Text from '@/components/ui/custom/dataDisplay/Text';
import i18n from 'i18next';

type Props = {
  slugCourseName: string;
  klassId?: number;
  loading?: boolean;
  lesson?: Nullable<Lesson>;
  date?: string;
  classroomUrl?: string;
};

const NextLessonDisplay = memo((props: Props) => {
  const {
    slugCourseName,
    klassId,
    loading,
    lesson,
    date,
    classroomUrl = '',
  } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'nextLessonDisplay',
  });

  const isLg = useMediaQuery({ minWidth: Breakpoints.md });

  if (loading) {
    return <LoadingIcon className="w-6 h-6 text-primary self-center" />;
  }

  if (!lesson) {
    return null;
  }

  return (
    <section className="flex flex-col gap-2 w-full">
      <section className="gap-1.5 justify-between flex flex-col md:flex-row w-full">
        <div className="flex flex-col justify-start">
          <h2
            className={twJoin(
              subheading2,
              'text-base-content font-600 capitalize h-fit',
            )}
          >
            {t('title')}
          </h2>

          <ConditionalRenderer condition={!isLg}>
            <Countdown
              date={date}
              days
              hours
              minutes
              seconds
              className={{
                base: 'self-center',
              }}
            />
          </ConditionalRenderer>
        </div>
        <ConditionalRenderer condition={!isLg}>
          <div className="flex gap-2 flex-col md:flex-row">
            <LessonLink
              slugCourseName={slugCourseName}
              lesson={lesson}
              klassId={klassId}
            />
            <ClassroomLink classroomUrl={classroomUrl} date={date} />
          </div>
        </ConditionalRenderer>

        <ConditionalRenderer condition={isLg}>
          <div className="flex flex-col gap-2">
            <Countdown date={date} days hours minutes seconds />
            <ClassroomLink classroomUrl={classroomUrl} date={date} />
          </div>
        </ConditionalRenderer>
      </section>
      <ConditionalRenderer condition={isLg}>
        <LessonLink
          slugCourseName={slugCourseName}
          lesson={lesson}
          klassId={klassId}
        />
      </ConditionalRenderer>
    </section>
  );
});

export default NextLessonDisplay;

type LessonLinkProps = {
  slugCourseName: string;
  lesson: Lesson;
  klassId?: number;
};

const LessonLink = memo((props: LessonLinkProps) => {
  const { slugCourseName, lesson, klassId } = props;

  return (
    <Link
      className="text-primary font-600 underline transition-all duration-300 ease-in-out hover:text-primary/50"
      to={ROUTES.COURSES.LESSON.INFO(slugCourseName, lesson.id, klassId)}
    >
      {lesson.name}
    </Link>
  );
});

type ClassroomLinkProps = {
  classroomUrl: string;
  date?: string;
};

const ClassroomLink = memo((props: ClassroomLinkProps) => {
  const { classroomUrl, date } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'nextLessonDisplay',
  });

  const formatDateByLanguage = (dateString: string) => {
    const isPortuguese = i18n.language === 'pt_BR';

    if (isPortuguese) {
      return moment(dateString).format(`DD/MM [${t('at')}] HH[h]`);
    } else {
      return moment(dateString).format(`MM/DD [${t('at')}] h:mm A`);
    }
  };

  const formattedDate = date ? formatDateByLanguage(date) : '';

  return (
    <ConditionalRenderer condition={classroomUrl}>
      <span className="inline-flex items-center gap-2 self-center">
        <Text text={formattedDate} />
        <a href={classroomUrl} target="_blank" rel="noopener noreferrer">
          <IconWithText
            className={{
              base: 'flex-row-reverse gap-1 text-primary',
              text: 'font-600 text-[1rem]',
            }}
            icon={<ArrowTopRightOnSquareIcon className="w-4 h-4 stroke-2" />}
            text={t('classroom')}
          />
        </a>
      </span>
    </ConditionalRenderer>
  );
});
